import { Alert, Box, Card, IconButton, Text } from '@chakra-ui/react'
import { ArrowLeft } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { getLocalizedDeleteScheduleErrorMessage } from '~/pages/Schedules/scheduleDeleteErrorMessage'
import {
  getLocalizedScheduleQueryError,
  localizeScheduleQueryError,
} from '~/pages/Schedules/scheduleQueryErrorMessage'
import { useScheduleDetailCacheLifecycle } from '~/pages/Schedules/useScheduleDetailCacheLifecycle'
import {
  useCreateScheduleEvent,
  useDeleteSchedule,
  useDeleteScheduleEvent,
  useGetSchedule,
  useGetScheduleEvents,
  useGetScheduleHolidays,
  useResolveScheduleRouteId,
  useUpdateSchedule,
  useUpdateScheduleEvent,
} from '~/queries/scheduleQueries'

import { getTimeZoneLabel } from '@mangovoice/constants'
import { LoadingState } from '@mangovoice/mango-ui'
import {
  AddHolidaysModal,
  createWeeklyHoursEvent,
  EditOverrideModal,
  EditScheduleModal,
  processScheduleEvents,
  ScheduleDetail,
  ScheduleI18nProvider,
  updateWeeklyHoursEvent,
} from '@mangovoice/schedule'
import type {
  ScheduleEventCreate,
  ScheduleEventId,
  ScheduleEventUpdate,
  ScheduleUpdate,
} from '@mangovoice/schedule'

interface ApiError {
  code: string
  message: string
}

/**
 * Convert snake_case error code to Title Case
 */
const formatErrorCode = (code: string): string => {
  return code
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const ErrorState: React.FC<{
  error: ApiError
  fallbackTitle: string
}> = ({ error, fallbackTitle }) => {
  return (
    <Alert.Root status="error" p={4}>
      <Alert.Indicator />
      <Box>
        <Text fontWeight="bold">
          {formatErrorCode(error.code || fallbackTitle)}
        </Text>
        <Text>{error.message}</Text>
      </Box>
    </Alert.Root>
  )
}

export const ScheduleDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const checkRole = useCheckRole()
  const canDelete = checkRole('senior')
  const scheduleLocale = i18n.resolvedLanguage ?? i18n.language
  const schedulesListPath = ApplicationRoutes.SCHEDULES
  const {
    scheduleId,
    isLegacyRoute,
    isLoading: isResolvingLegacyRoute,
    error: legacyRouteError,
  } = useResolveScheduleRouteId(id)
  const { invalidateScheduleLists, markScheduleDeleted } =
    useScheduleDetailCacheLifecycle(scheduleId)

  React.useEffect(() => {
    if (isLegacyRoute && scheduleId) {
      navigate(`${schedulesListPath}/${scheduleId}`, { replace: true })
    }
  }, [isLegacyRoute, navigate, scheduleId, schedulesListPath])

  const {
    data: schedule,
    isLoading: isLoadingSchedule,
    error: assetScheduleError,
    refetch: refetchSchedule,
  } = useGetSchedule({
    id: scheduleId,
  })
  const scheduleError = legacyRouteError ?? assetScheduleError

  // Handle schedule error
  React.useEffect(() => {
    if (scheduleError) {
      toaster.create({
        title: t(
          'schedule.detail.toast.loadScheduleFailedTitle',
          'Failed to load schedule',
        ),
        description: getLocalizedScheduleQueryError({
          error: scheduleError,
          fallbackMessage: t(
            'schedule.detail.toast.loadScheduleFailedDescription',
            'An error occurred while loading the schedule',
          ),
          t,
        }),
        type: 'error',
        duration: 5000,
        closable: true,
      })
    }
  }, [scheduleError, t])

  const {
    data: events = [],
    isLoading: isLoadingEvents,
    error: eventsError,
    refetch: refetchEvents,
  } = useGetScheduleEvents({
    scheduleId,
    options: {
      enabled: !!scheduleId && !!schedule,
    },
  })

  // Handle events error
  React.useEffect(() => {
    if (eventsError) {
      toaster.create({
        title: t(
          'schedule.detail.toast.loadEventsFailedTitle',
          'Failed to load schedule events',
        ),
        description: getLocalizedScheduleQueryError({
          error: eventsError,
          fallbackMessage: t(
            'schedule.detail.toast.loadEventsFailedDescription',
            'An error occurred while loading events',
          ),
          t,
        }),
        type: 'error',
        duration: 5000,
        closable: true,
      })
    }
  }, [eventsError, t])

  // Mutations
  // Note: No global onSuccess/onError - toasts handled at call sites for batch support
  const createEventMutation = useCreateScheduleEvent({
    scheduleId,
  })

  // Note: No global onSuccess/onError - toasts handled at call sites for batch support
  const updateEventMutation = useUpdateScheduleEvent({
    scheduleId,
  })

  const deleteEventMutation = useDeleteScheduleEvent({
    scheduleId,
    options: {
      onSuccess: () => {
        toaster.create({
          title: t('schedule.detail.toast.successTitle', 'Success'),
          description: t(
            'schedule.detail.toast.eventDeleted',
            'Event deleted successfully',
          ),
          type: 'success',
          duration: 3000,
          closable: true,
        })
        // Refetch schedule to update routing status
        refetchSchedule()
      },
      onError: (error) => {
        toaster.create({
          title: t(
            'schedule.detail.toast.deleteEventFailedTitle',
            'Failed to delete event',
          ),
          description: getLocalizedScheduleQueryError({
            error,
            fallbackMessage: t(
              'schedule.detail.toast.genericError',
              'An error occurred',
            ),
            t,
          }),
          type: 'error',
          duration: 5000,
          closable: true,
        })
      },
    },
  })

  const deleteScheduleMutation = useDeleteSchedule({
    options: {
      onSuccess: () => {
        markScheduleDeleted()
        toaster.create({
          title: t('schedule.detail.toast.successTitle', 'Success'),
          description: t(
            'schedule.detail.toast.scheduleDeleted',
            'Schedule deleted successfully',
          ),
          type: 'success',
          duration: 3000,
          closable: true,
        })
        navigate(schedulesListPath)
      },
      onError: (error) => {
        toaster.create({
          title: t(
            'schedule.detail.toast.deleteScheduleFailedTitle',
            'Failed to delete schedule',
          ),
          description: getLocalizedDeleteScheduleErrorMessage({
            error,
            fallbackMessage: t(
              'schedule.detail.toast.genericError',
              'An error occurred',
            ),
            t,
          }),
          type: 'error',
          duration: 7000,
          closable: true,
        })
      },
    },
  })

  const updateScheduleMutation = useUpdateSchedule({
    invalidateQueries: false,
    options: {
      onSuccess: () => {
        toaster.create({
          title: t('schedule.detail.toast.successTitle', 'Success'),
          description: t(
            'schedule.detail.toast.scheduleUpdated',
            'Schedule updated successfully',
          ),
          type: 'success',
          duration: 3000,
          closable: true,
        })
      },
      onError: (error) => {
        toaster.create({
          title: t(
            'schedule.detail.toast.updateScheduleFailedTitle',
            'Failed to update schedule',
          ),
          description: getLocalizedScheduleQueryError({
            error,
            fallbackMessage: t(
              'schedule.detail.toast.genericError',
              'An error occurred',
            ),
            t,
          }),
          type: 'error',
          duration: 5000,
          closable: true,
        })
      },
    },
  })

  // State for edit schedule modal
  const [isEditScheduleModalOpen, setIsEditScheduleModalOpen] =
    React.useState(false)

  // Event handlers
  const handleCreateWeeklyHours = useCallback(
    async (start: Date, end: Date) => {
      if (!schedule) return
      const eventData = createWeeklyHoursEvent(start, end, schedule.time_zone)
      try {
        await createEventMutation.mutateAsync(eventData)
        toaster.create({
          title: t('schedule.detail.toast.successTitle', 'Success'),
          description: t(
            'schedule.detail.toast.weeklyHoursCreated',
            'Weekly hours created successfully',
          ),
          type: 'success',
          duration: 3000,
          closable: true,
        })
        // Refetch schedule to update routing status
        refetchSchedule()
      } catch (error) {
        toaster.create({
          title: t(
            'schedule.detail.toast.createWeeklyHoursFailedTitle',
            'Failed to create weekly hours',
          ),
          description:
            (error as { message?: string })?.message ||
            t('schedule.detail.toast.genericError', 'An error occurred'),
          type: 'error',
          duration: 5000,
          closable: true,
        })
      }
    },
    [schedule, createEventMutation, refetchSchedule, t],
  )

  const handleUpdateWeeklyHours = useCallback(
    (eventId: ScheduleEventId, start: Date, end: Date) => {
      if (!schedule) return
      const eventData = updateWeeklyHoursEvent(start, end, schedule.time_zone)
      updateEventMutation.mutate(
        { eventId, data: eventData },
        {
          onSuccess: () => {
            toaster.create({
              title: t('schedule.detail.toast.successTitle', 'Success'),
              description: t(
                'schedule.detail.toast.weeklyHoursUpdated',
                'Weekly hours updated successfully',
              ),
              type: 'success',
              duration: 3000,
              closable: true,
            })
            // Refetch schedule to update routing status
            refetchSchedule()
          },
          onError: (error) => {
            toaster.create({
              title: t(
                'schedule.detail.toast.updateWeeklyHoursFailedTitle',
                'Failed to update weekly hours',
              ),
              description: getLocalizedScheduleQueryError({
                error,
                fallbackMessage: t(
                  'schedule.detail.toast.genericError',
                  'An error occurred',
                ),
                t,
              }),
              type: 'error',
              duration: 5000,
              closable: true,
            })
          },
        },
      )
    },
    [schedule, updateEventMutation, refetchSchedule, t],
  )

  const handleDeleteWeeklyHours = useCallback(
    (eventId: ScheduleEventId) => {
      deleteEventMutation.mutate(eventId)
    },
    [deleteEventMutation],
  )

  const handleDeleteOverride = useCallback(
    (eventId: ScheduleEventId, _eventName: string) => {
      deleteEventMutation.mutate(eventId)
    },
    [deleteEventMutation],
  )

  const handleEditInfo = useCallback(() => {
    setIsEditScheduleModalOpen(true)
  }, [])

  const handleCloseEditScheduleModal = useCallback(() => {
    setIsEditScheduleModalOpen(false)
  }, [])

  const handleSaveScheduleInfo = useCallback(
    async (data: ScheduleUpdate, updateCustomEvents: boolean) => {
      if (!schedule) return

      const timezoneChanged = data.time_zone !== schedule.time_zone
      const eventsToUpdate = timezoneChanged
        ? events.filter(
            (event) => updateCustomEvents || event.fbtype === 'FREE',
          )
        : []
      const updateData: ScheduleUpdate =
        timezoneChanged && data.time_zone
          ? {
              ...data,
              event_time_zone_scope: updateCustomEvents
                ? 'all'
                : 'weekly_hours',
            }
          : data

      try {
        await updateScheduleMutation.mutateAsync({
          id: scheduleId,
          data: updateData,
        })

        await Promise.all([refetchSchedule(), refetchEvents()])
        await invalidateScheduleLists()

        if (eventsToUpdate.length > 0) {
          toaster.create({
            title: t(
              'schedule.detail.toast.eventsUpdatedTitle',
              'Events Updated',
            ),
            description: t(
              'schedule.detail.toast.eventsUpdatedDescription',
              'Updated timezone for {{count}} event(s)',
              { count: eventsToUpdate.length },
            ),
            type: 'info',
            duration: 3000,
            closable: true,
          })
        }

        setIsEditScheduleModalOpen(false)
      } catch {
        // The mutation's configured error handler reports the parent failure.
      }
    },
    [
      schedule,
      scheduleId,
      events,
      updateScheduleMutation,
      refetchSchedule,
      refetchEvents,
      invalidateScheduleLists,
      t,
    ],
  )

  const handleDeleteSchedule = useCallback(() => {
    if (!schedule) return
    deleteScheduleMutation.mutate(scheduleId)
  }, [schedule, scheduleId, deleteScheduleMutation])

  const handleAddOverride = useCallback(() => {
    setOverrideModalMode('create')
    setEditingEventId(null)
  }, [])

  // Prefetch templates so edit modal can lock irregular holidays immediately.
  const [isHolidaysModalOpen, setIsHolidaysModalOpen] = React.useState(false)
  const {
    data: holidays = [],
    isLoading: isLoadingHolidays,
    error: holidaysError,
  } = useGetScheduleHolidays({
    options: {
      enabled: !!schedule,
    },
  })

  const handleAddHolidays = useCallback(() => {
    setIsHolidaysModalOpen(true)
  }, [])

  const handleCloseHolidaysModal = useCallback(() => {
    setIsHolidaysModalOpen(false)
  }, [])

  const handleBulkCreateHolidays = useCallback(
    async (holidays: ScheduleEventCreate[]) => {
      // Create all holidays sequentially
      let successCount = 0
      let errorCount = 0

      for (const holiday of holidays) {
        try {
          await new Promise<void>((resolve, _reject) => {
            createEventMutation.mutate(holiday, {
              onSuccess: () => {
                successCount++
                resolve()
              },
              onError: () => {
                errorCount++
                resolve() // Don't reject, continue with other holidays
              },
            })
          })
        } catch {
          errorCount++
        }
      }

      // Show summary toast
      if (successCount > 0) {
        toaster.create({
          title: t('schedule.detail.toast.successTitle', 'Success'),
          description:
            errorCount > 0
              ? t(
                  'schedule.detail.toast.holidaysAddedWithFailures',
                  '{{successCount}} holidays added successfully ({{errorCount}} failed)',
                  { successCount, errorCount },
                )
              : t(
                  'schedule.detail.toast.holidaysAddedSuccess',
                  '{{count}} holidays added successfully',
                  { count: successCount },
                ),
          type: 'success',
          duration: 3000,
          closable: true,
        })
        // Refetch schedule to update routing status
        refetchSchedule()
      } else if (errorCount > 0) {
        toaster.create({
          title: t('schedule.detail.toast.failedTitle', 'Failed'),
          description: t(
            'schedule.detail.toast.holidaysAddFailed',
            'Failed to add {{count}} holiday(s)',
            { count: errorCount },
          ),
          type: 'error',
          duration: 5000,
          closable: true,
        })
      }
    },
    [createEventMutation, refetchSchedule, t],
  )

  // State for override modal (supports both create and edit modes)
  const [overrideModalMode, setOverrideModalMode] = React.useState<
    'create' | 'edit' | null
  >(null)
  const [editingEventId, setEditingEventId] =
    React.useState<ScheduleEventId | null>(null)

  // Find the event and helper for editing
  const editingEvent = React.useMemo(() => {
    if (!editingEventId) return null
    return events.find((e) => e.id === editingEventId) || null
  }, [editingEventId, events])

  const editingEventHelper = React.useMemo(() => {
    if (!editingEvent || !schedule) return null
    const helpers = processScheduleEvents([editingEvent], schedule.time_zone)
    return helpers.find((h) => h.getId() === editingEventId) || null
  }, [editingEvent, editingEventId, schedule])

  const handleEditOverride = useCallback((eventId: ScheduleEventId) => {
    setOverrideModalMode('edit')
    setEditingEventId(eventId)
  }, [])

  const handleCloseOverrideModal = useCallback(() => {
    setOverrideModalMode(null)
    setEditingEventId(null)
  }, [])

  const handleSaveOverride = useCallback(
    (eventId: ScheduleEventId, data: ScheduleEventUpdate) => {
      const apiData: ScheduleEventUpdate = { ...data }

      updateEventMutation.mutate(
        {
          eventId,
          data: apiData,
        },
        {
          onSuccess: () => {
            toaster.create({
              title: t('schedule.detail.toast.successTitle', 'Success'),
              description: t(
                'schedule.detail.toast.overrideUpdated',
                'Override updated successfully',
              ),
              type: 'success',
              duration: 3000,
              closable: true,
            })
            // Refetch schedule to update routing status
            refetchSchedule()
          },
          onError: (error: ApiError) => {
            toaster.create({
              title: t(
                'schedule.detail.toast.updateOverrideFailedTitle',
                'Failed to update override',
              ),
              description: getLocalizedScheduleQueryError({
                error,
                fallbackMessage: t(
                  'schedule.detail.toast.genericError',
                  'An error occurred',
                ),
                t,
              }),
              type: 'error',
              duration: 5000,
              closable: true,
            })
          },
        },
      )
    },
    [updateEventMutation, refetchSchedule, t],
  )

  const handleCreateOverride = useCallback(
    (data: ScheduleEventCreate) => {
      createEventMutation.mutate(data, {
        onSuccess: () => {
          toaster.create({
            title: t('schedule.detail.toast.successTitle', 'Success'),
            description: t(
              'schedule.detail.toast.overrideCreated',
              'Override created successfully',
            ),
            type: 'success',
            duration: 3000,
            closable: true,
          })
          // Refetch schedule to update routing status
          refetchSchedule()
        },
        onError: (error) => {
          toaster.create({
            title: t(
              'schedule.detail.toast.createOverrideFailedTitle',
              'Failed to create override',
            ),
            description: getLocalizedScheduleQueryError({
              error,
              fallbackMessage: t(
                'schedule.detail.toast.genericError',
                'An error occurred',
              ),
              t,
            }),
            type: 'error',
            duration: 5000,
            closable: true,
          })
        },
      })
    },
    [createEventMutation, refetchSchedule, t],
  )

  // Format timezone name
  const timeZoneName = useMemo(() => {
    if (!schedule) return undefined
    return getTimeZoneLabel(schedule.time_zone)
  }, [schedule])

  const isLoading =
    isResolvingLegacyRoute || isLoadingSchedule || isLoadingEvents
  const error = scheduleError || eventsError

  if (isLoading) {
    return (
      <ScheduleI18nProvider t={t} locale={scheduleLocale}>
        <Box
          py={6}
          px={0}
          h="full"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
        >
          <Card.Root>
            <Card.Body p={6}>
              <LoadingState
                layout="alert"
                title={t('schedule.detail.loading', 'Loading schedule...')}
              />
            </Card.Body>
          </Card.Root>
        </Box>
      </ScheduleI18nProvider>
    )
  }

  if (error) {
    return (
      <ScheduleI18nProvider t={t} locale={scheduleLocale}>
        <Box
          py={6}
          px={0}
          h="full"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
        >
          <Card.Root>
            <Card.Body p={6}>
              <ErrorState
                error={localizeScheduleQueryError(error, t) ?? error}
                fallbackTitle={t('schedule.detail.error', 'Error')}
              />
            </Card.Body>
          </Card.Root>
        </Box>
      </ScheduleI18nProvider>
    )
  }

  if (!schedule) {
    return (
      <ScheduleI18nProvider t={t} locale={scheduleLocale}>
        <Box
          py={6}
          px={0}
          h="full"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
        >
          <Card.Root>
            <Card.Body p={6}>
              <Alert.Root status="warning" p={4}>
                <Alert.Indicator />
                <Box>
                  <Text fontWeight="bold">
                    {t('schedule.detail.notFound.title', 'Schedule not found')}
                  </Text>
                  <Text>
                    {t(
                      'schedule.detail.notFound.description',
                      'The schedule you are looking for does not exist.',
                    )}
                  </Text>
                </Box>
              </Alert.Root>
            </Card.Body>
          </Card.Root>
        </Box>
      </ScheduleI18nProvider>
    )
  }

  return (
    <ScheduleI18nProvider t={t} locale={scheduleLocale}>
      <Box px={0} h="full" minH={0} overflowY="auto" overflowX="hidden">
        <ScheduleDetail
          schedule={schedule}
          events={events}
          timeZoneName={timeZoneName}
          canNew={true}
          canEdit={true}
          canDelete={canDelete}
          backButton={
            <IconButton
              aria-label={t(
                'schedule.detail.backToSchedules',
                'Back to Schedules',
              )}
              variant="ghost"
              size="sm"
              onClick={() => navigate(schedulesListPath)}
            >
              <ArrowLeft />
            </IconButton>
          }
          onEditInfo={handleEditInfo}
          onDeleteSchedule={canDelete ? handleDeleteSchedule : undefined}
          onCreateWeeklyHours={handleCreateWeeklyHours}
          onUpdateWeeklyHours={handleUpdateWeeklyHours}
          onDeleteWeeklyHours={canDelete ? handleDeleteWeeklyHours : undefined}
          onAddOverride={handleAddOverride}
          onAddHolidays={handleAddHolidays}
          onEditOverride={handleEditOverride}
          onDeleteOverride={canDelete ? handleDeleteOverride : undefined}
        />

        {/* Override Modal (Create/Edit) */}
        {schedule && (
          <EditOverrideModal
            isOpen={overrideModalMode !== null}
            onClose={handleCloseOverrideModal}
            mode={overrideModalMode || 'edit'}
            event={editingEvent}
            eventHelper={editingEventHelper}
            scheduleTimeZone={schedule.time_zone}
            holidayTemplates={holidays}
            areHolidayTemplatesLoading={isLoadingHolidays}
            holidayTemplatesError={localizeScheduleQueryError(holidaysError, t)}
            onSave={handleSaveOverride}
            onCreate={handleCreateOverride}
            onNotify={(notification) => {
              toaster.create({
                title: notification.title,
                description: notification.description,
                type: notification.type,
                duration: notification.duration ?? 3000,
                closable: notification.closable ?? true,
              })
            }}
          />
        )}

        {/* Add Holidays Modal */}
        {schedule && (
          <AddHolidaysModal
            isOpen={isHolidaysModalOpen}
            onClose={handleCloseHolidaysModal}
            scheduleTimeZone={schedule.time_zone}
            holidays={holidays}
            isLoadingHolidays={isLoadingHolidays}
            holidaysError={localizeScheduleQueryError(holidaysError, t)}
            onAddHolidays={handleBulkCreateHolidays}
            onOpenCustomOverride={handleAddOverride}
            onNotify={(notification) => {
              toaster.create({
                title: notification.title,
                description: notification.description,
                type: notification.type,
                duration: notification.duration ?? 3000,
                closable: notification.closable ?? true,
              })
            }}
          />
        )}

        {/* Edit Schedule Modal */}
        {schedule && (
          <EditScheduleModal
            isOpen={isEditScheduleModalOpen}
            onClose={handleCloseEditScheduleModal}
            schedule={schedule}
            events={events}
            onSave={handleSaveScheduleInfo}
            isSaving={updateScheduleMutation.isPending}
            onNotify={(notification) => {
              toaster.create({
                title: notification.title,
                description: notification.description,
                type: notification.type,
                duration: notification.duration ?? 3000,
                closable: notification.closable ?? true,
              })
            }}
          />
        )}
      </Box>
    </ScheduleI18nProvider>
  )
}
